import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { alertRulesActions } from './slice';

/**
 * Alert Rules saga (P4a — My Alerts). CRUD against the v5 /alerts/rules API (personal rules —
 * the server derives the owner from the token). On mutate failure we toast + re-pull the list so
 * the UI reflects the server's truth. Feature-gated on `personalAlerts` at the route level.
 */
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const okStatus = (res) => res && (res.statusCode === 200 || res.statusCode === 201);

function* fetchRules() {
	const orgID = yield select(selectOrgID);
	yield put(alertRulesActions.loadRulesStart({ orgID }));
	try {
		const res = yield call(() => SuperFetch.get('/alerts/rules', {}, { type: 'personal' }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load alerts.');
		yield put(alertRulesActions.loadRulesSuccess({ orgID, rules: res.data || [] }));
	} catch (error) {
		yield put(alertRulesActions.loadRulesError({ orgID, error: error.message || String(error) }));
	}
}

// saveRule handles both create (no alertRuleID) and update (with alertRuleID).
function* saveRule(action) {
	const orgID = yield select(selectOrgID);
	const { alertRuleID, rule } = action.payload;
	try {
		const res = alertRuleID
			? yield call(() => SuperFetch.patch(`/alerts/rules/${alertRuleID}`, rule))
			: yield call(() => SuperFetch.post('/alerts/rules', { ...rule, type: 'personal' }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to save alert.');
		yield put(alertRulesActions.saveRuleSuccess({ orgID, rule: res.data }));
		Notification('success', alertRuleID ? 'Alert updated.' : 'Alert created.');
	} catch (error) {
		Notification('error', 'Could not save the alert.');
		yield put(alertRulesActions.saveRuleError({ orgID }));
		yield put(alertRulesActions.loadRules({ orgID }));
	}
}

// Fire-and-report: no list to reconcile on failure (unlike save/delete), just a toast.
function* testFire(action) {
	const orgID = yield select(selectOrgID);
	const { eventType, deliveryLevel } = action.payload;
	try {
		const res = yield call(() =>
			SuperFetch.post('/alerts/rules/test-fire', { type: 'personal', eventType, deliveryLevel })
		);
		if (!okStatus(res)) throw new Error(res.message || 'Failed to send the test.');
		yield put(alertRulesActions.testFireSuccess({ orgID }));
		Notification(
			res.data?.recipients ? 'success' : 'info',
			res.data?.recipients
				? 'Test notification sent — check your inbox.'
				: 'No recipients to notify with this configuration.'
		);
	} catch (error) {
		Notification('error', 'Could not send the test notification.');
		yield put(alertRulesActions.testFireError({ orgID }));
	}
}

function* removeRule(action) {
	const orgID = yield select(selectOrgID);
	const { alertRuleID } = action.payload;
	try {
		const res = yield call(() => SuperFetch.delete(`/alerts/rules/${alertRuleID}`));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to delete alert.');
		yield put(alertRulesActions.deleteRuleSuccess({ orgID, alertRuleID }));
		Notification('success', 'Alert deleted.');
	} catch (error) {
		Notification('error', 'Could not delete the alert.');
		yield put(alertRulesActions.deleteRuleError({ orgID }));
		yield put(alertRulesActions.loadRules({ orgID }));
	}
}

export default function* alertRulesSaga() {
	yield all([
		takeLatest(alertRulesActions.loadRules, fetchRules),
		takeEvery(alertRulesActions.saveRule, saveRule),
		takeEvery(alertRulesActions.deleteRule, removeRule),
		takeEvery(alertRulesActions.testFire, testFire)
	]);
}
